/**
 * GP Ads AI - Budget Adjustment API Client
 * 
 * PRD-3604: E004-S004 - Adjust Campaign Budgets
 */

import { apiClient } from './apiClient';

// Types
export interface BudgetAdjustmentRequest {
  campaignId: string;
  platform: 'meta' | 'google' | 'tiktok';
  newBudget: number;
  oldBudget: number;
  reason?: string;
  customerId?: string; // Google
  budgetId?: string; // Google
  advertiserId?: string; // TikTok
}

export interface BudgetAdjustmentResponse {
  action_id: string;
  status: 'approved' | 'pending_approval' | 'rejected';
  requires_approval: boolean;
  approval_level?: string;
  expected_impact?: string;
  estimated_savings?: number;
}

export interface BudgetValidationResult {
  valid: boolean;
  error?: string;
  old_budget: number;
  new_budget: number;
  change_amount: number;
  change_percent: number;
  approval_required: boolean;
  approval_level: string;
  warnings: string[];
  limits: {
    min_budget: number;
    max_budget: number | null;
    max_auto_approve: number;
  };
}

export interface CampaignBudget {
  campaign_id: string;
  campaign_name: string;
  platform: string;
  current_budget: number;
  budget_type: 'daily' | 'lifetime';
  currency: string;
}

// API Functions
export const budgetApi = {
  /**
   * Validate a budget change before submitting
   */
  validateBudgetChange: async (
    oldBudget: number,
    newBudget: number
  ): Promise<BudgetValidationResult> => {
    const response = await apiClient.get<BudgetValidationResult>(
      '/api/v1/budget/validate',
      {
        params: { old_budget: oldBudget, new_budget: newBudget }
      }
    );
    return response.data;
  },

  /**
   * Request a budget adjustment
   */
  adjustBudget: async (
    request: BudgetAdjustmentRequest
  ): Promise<BudgetAdjustmentResponse> => {
    const response = await apiClient.post<BudgetAdjustmentResponse>(
      '/api/v1/actions/',
      {
        action_type: 'adjust_budget',
        platform: request.platform,
        campaign_id: request.campaignId,
        parameters: {
          new_budget: request.newBudget,
          old_budget: request.oldBudget,
          reason: request.reason,
          customer_id: request.customerId,
          budget_id: request.budgetId,
          advertiser_id: request.advertiserId
        }
      }
    );
    return response.data;
  },

  /**
   * Get current budget for a campaign
   */
  getCampaignBudget: async (campaignId: string): Promise<CampaignBudget> => {
    const response = await apiClient.get<CampaignBudget>(
      `/api/v1/campaigns/${campaignId}/budget`
    );
    return response.data;
  },

  /**
   * Get quick adjustment presets based on current budget
   */
  getQuickAdjustments: (currentBudget: number) => {
    return [
      { label: '-25%', value: currentBudget * 0.75 },
      { label: '-10%', value: currentBudget * 0.90 },
      { label: '+10%', value: currentBudget * 1.10 },
      { label: '+25%', value: currentBudget * 1.25 },
      { label: '+50%', value: currentBudget * 1.50 },
      { label: '2x', value: currentBudget * 2 }
    ];
  },

  /**
   * Calculate change percentage
   */
  calculateChangePercent: (oldBudget: number, newBudget: number): number => {
    if (oldBudget === 0) return 0;
    return ((newBudget - oldBudget) / oldBudget) * 100;
  },

  /**
   * Check if approval is required (client-side estimate)
   */
  requiresApproval: (oldBudget: number, newBudget: number): boolean => {
    const changeAmount = Math.abs(newBudget - oldBudget);
    const changePercent = Math.abs(
      budgetApi.calculateChangePercent(oldBudget, newBudget)
    );
    
    // PRD-3604 rules: >20% OR >$1,000 requires approval
    return changePercent > 20 || changeAmount > 1000;
  },

  /**
   * Get approval level (client-side estimate)
   */
  getApprovalLevel: (
    oldBudget: number,
    newBudget: number
  ): 'auto_approve' | 'manager_approval' | 'director_approval' => {
    const changeAmount = Math.abs(newBudget - oldBudget);
    const changePercent = Math.abs(
      budgetApi.calculateChangePercent(oldBudget, newBudget)
    );

    // $10,000+ requires director
    if (changeAmount > 10000) {
      return 'director_approval';
    }

    // >20% OR >$1,000 requires manager
    if (changePercent > 20 || changeAmount > 1000) {
      return 'manager_approval';
    }

    return 'auto_approve';
  },

  /**
   * Format budget for display
   */
  formatBudget: (amount: number, currency = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  },

  /**
   * Format change percent for display
   */
  formatChangePercent: (percent: number): string => {
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(1)}%`;
  }
};

export default budgetApi;
